import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { IncidentDetailPatient } from '../useIncidentDetailForHiroshima';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { AccordionToggle } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggle';
import { useOpenClose } from 'core/hooks/useOpenClose';
import { AccordionToggleContent } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggleContent';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import {
  convertNumberToString,
  convertToPresenceAbsence,
} from 'core/components/common/converters';
import { formatDateTime } from 'core/utils/date';
import { DataNotFound } from 'core/components/common/DataNotFound';
import { FC } from 'react';

type Props = {
  /** バイタルリスト。 */
  vitals: IncidentDetailPatient['vitals'];
};

export const VitalsView: FC<Props> = ({ vitals }) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="バイタル" />
        <div className="flex flex-col gap-[8px]">
          {vitals.length > 0 ? (
            vitals.map(vital => (
              <VitalDetailView key={vital.vitalId} vitalData={vital.data} />
            ))
          ) : (
            <DataNotFound />
          )}
        </div>
      </div>
    </BorderedContainer>
  );
};

type VitalDetailViewProps = {
  /** バイタル。 */
  vitalData: IncidentDetailPatient['vitals'][number]['data'];
};

const VitalDetailView = ({ vitalData }: VitalDetailViewProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  return (
    <>
      <AccordionToggle
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
      >
        <p className="text-[16px] noto-sans-jp-medium text-textPrimary">
          {vitalData.recordedAt
            ? formatDateTime(vitalData.recordedAt, 'MM/dd HH:mm')
            : '時間未指定'}
        </p>
      </AccordionToggle>
      <AccordionToggleContent isOpen={isOpen}>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="バイタル測定時の活動場面"
            content={vitalData.measureTiming}
          />
          <InfoDisplay title="JCS" content={vitalData.jcs} />
          <InfoDisplay
            title="R"
            content={convertToPresenceAbsence(vitalData?.riaR)}
          />
          <InfoDisplay
            title="I"
            content={convertToPresenceAbsence(vitalData?.riaI)}
          />
          <InfoDisplay
            title="A"
            content={convertToPresenceAbsence(vitalData?.riaA)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="E(GCS)" content={vitalData.gcsE ?? ''} />
          <InfoDisplay title="V(GCS)" content={vitalData.gcsV ?? ''} />
          <InfoDisplay title="M(GCS)" content={vitalData.gcsM ?? ''} />
          <InfoDisplay title="合計(GCS)" content={vitalData.gcsTotal ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="PR"
            content={convertNumberToString(vitalData?.pr)}
          />
          <InfoDisplay
            title="不整"
            content={convertToPresenceAbsence(vitalData?.isArrhythmia)}
          />
          <InfoDisplay
            title="触知不能"
            content={convertToPresenceAbsence(vitalData?.isUnpalpable)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BP(右)/収縮(mmHg)"
            content={convertNumberToString(vitalData?.sbpRight)}
          />
          <InfoDisplay
            title="BP(右)/拡張(mmHg)"
            content={convertNumberToString(vitalData?.dbpRight)}
          />
          <InfoDisplay
            title="BP(左)/収縮(mmHg)"
            content={convertNumberToString(vitalData?.sbpLeft)}
          />
          <InfoDisplay
            title="BP(左)/拡張(mmHg)"
            content={convertNumberToString(vitalData?.dbpLeft)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="RR"
            content={vitalData.rr ? `${vitalData.rr} 回/min` : ''}
          />
          <InfoDisplay
            title="異常呼吸"
            content={convertToPresenceAbsence(vitalData.isRespirationAbnormal)}
          />
          <InfoDisplay
            title="左右差"
            content={convertToPresenceAbsence(vitalData.isRespirationUnequal)}
          />
          <InfoDisplay
            title="減弱(左)"
            content={convertToPresenceAbsence(
              vitalData.isRespirationDecreasedLeft
            )}
          />
          <InfoDisplay
            title="減弱(右)"
            content={convertToPresenceAbsence(
              vitalData.isRespirationDecreasedRight
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="Sp02(room air)"
            content={vitalData.spo2 ? `${vitalData.spo2} %` : ''}
          />
          <InfoDisplay
            title="Sp02"
            content={vitalData.spo2After ? `${vitalData.spo2After} %` : ''}
          />
          <InfoDisplay
            title="酸素投与"
            content={vitalData.o2Flow ? `${vitalData.o2Flow} L` : ''}
          />
          <InfoDisplay title="器具" content={vitalData.o2Means} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BT"
            content={vitalData.bt ? `${vitalData.bt} °C` : ''}
          />
          <InfoDisplay title="体温測定場所" content={vitalData.btPart} />
          <InfoDisplay title="心電図波形" content={vitalData.ecgWaveform} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔(左)"
            content={
              vitalData.pupilSizeLeft ? `${vitalData.pupilSizeLeft} mm` : ''
            }
          />
          <InfoDisplay
            title="瞳孔(右)"
            content={
              vitalData.pupilSizeRight ? `${vitalData.pupilSizeRight} mm` : ''
            }
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="瞳孔(左)" content={vitalData.pupilPositionLeft} />
          <InfoDisplay
            title="瞳孔(右)"
            content={vitalData.pupilPositionRight}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="対光反射(左)"
            content={vitalData.pupilReactionLeft ?? ''}
          />
          <InfoDisplay
            title="対光反射(右)"
            content={vitalData.pupilReactionRight ?? ''}
          />
        </div>
      </AccordionToggleContent>
    </>
  );
};
